























































import {
  computed,
  defineComponent,
  onBeforeUpdate,
  onMounted,
  onUnmounted,
  onUpdated,
  PropType,
  ref,
  Ref,
} from "@vue/composition-api";
import { Vue } from "vue/types/vue";
import { LiveChatUser } from "@scrile/api-provider/dist/api/LivechatsProvider";
import { Message } from "@scrile/api-provider/dist/api/MessagesProvider";
import { TipSubjectType } from "@scrile/api-provider/dist/api/TipProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { checkRole } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import { getElement } from "@/lib/elementHelpers";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import config from "@/config";
import { StreamUserJoinData } from "@/types";
import { EVENTS } from "@/const";
import BaseIconButton from "@/components/BaseIconButton";
import BaseSendMessage from "@/components/BaseSendMessage";
import CustomScroll from "@/components/CustomScroll";
import SendMessageController from "@/components/SendMessageController";
import TipController from "@/components/TipController";
import TipIcon from "@/components/TipIcon";
import StreamMessage from "../StreamMessage";

function useKeepCustomScrollPosition(scrollNode: Ref<HTMLElement | undefined>) {
  const shouldKeepScroll = ref(false);
  const shouldScrollToBottom = ref(false);
  const prevScrollPos = ref({
    scrollHeight: scrollNode.value?.scrollHeight ?? 0,
    scrollTop: scrollNode.value?.scrollTop ?? 0,
    offsetHeight: scrollNode.value?.offsetHeight ?? 0,
  });

  // store current scroll data before each render
  onBeforeUpdate(() => {
    prevScrollPos.value = {
      scrollHeight: scrollNode.value?.scrollHeight ?? 0,
      scrollTop: scrollNode.value?.scrollTop ?? 0,
      offsetHeight: scrollNode.value?.offsetHeight ?? 0,
    };
  });

  const scrollToBottom = () => {
    setTimeout(() => {
      if (!scrollNode.value) return;
      if (scrollNode.value.scrollTo) {
        scrollNode.value.scrollTo(0, scrollNode.value.scrollHeight - scrollNode.value.offsetHeight);
      }
    });
  };
  const keepScrollPos = () => {
    if (!scrollNode.value) return;
    const fromBottomOffset = prevScrollPos.value.scrollHeight - prevScrollPos.value.scrollTop;
    if (scrollNode.value.scrollTo) {
      scrollNode.value.scrollTo(0, scrollNode.value.scrollHeight - fromBottomOffset);
    }
  };
  onUpdated(() => {
    if (scrollNode.value?.scrollHeight !== prevScrollPos.value.scrollHeight) {
      if (shouldKeepScroll.value) {
        keepScrollPos();
      } else if (shouldScrollToBottom.value) {
        scrollToBottom();
      }
    }
  });
  onUpdated(() => {
    shouldKeepScroll.value = false;
    shouldScrollToBottom.value = false;
  });
  return {
    shouldKeepScroll,
    shouldScrollToBottom,
    scrollNode,
  };
}

export default defineComponent({
  name: "StreamChat",
  components: {
    SendMessageController,
    BaseSendMessage,
    StreamMessage,
    CustomScroll,
    BaseIconButton,
    TipController,
    TipIcon,
  },
  props: {
    threadId: { type: String, required: true },
    me: { type: Object as PropType<LiveChatUser> },
    token: { type: String, required: true },
    sessionId: { type: String },
    tipRecipientId: { type: String },
    referrersMap: { type: Object as PropType<Record<string, UserPublicData>> },
  },
  setup(props, { emit }) {
    const baseSendMessage = ref();
    const messageListNode = ref<Vue | undefined>();
    const value = ref<string>("");
    const scrollbarWidth = ref(0);
    const sending = ref(false);
    const messageList = ref<Message[]>([]);
    const checkIsAdmin = (user: LiveChatUser) => {
      return checkRole(user?.user?.role ?? "", "admin");
    };

    const { authUser } = useAuthUser();
    const scrollNodeRef = computed<HTMLElement | undefined>(() => {
      if (!messageListNode.value) return undefined;
      if (messageListNode.value?.$refs?.customScroll && "SimpleBar" in messageListNode.value?.$refs?.customScroll) {
        return (messageListNode.value?.$refs.customScroll as Vue & { scrollElement: HTMLElement }).scrollElement;
      } else return getElement<HTMLElement>(messageListNode.value);
    });
    const { shouldScrollToBottom, scrollNode } = useKeepCustomScrollPosition(scrollNodeRef);

    let unsubscribe = () => {};

    const canTip = computed<boolean>(() => config.featureTippingLive && !(value.value.length > 0));

    const onNewMessageReceived = (message: Message) => {
      messageList.value = messageList.value.concat([message]);
      if (message.details?.type === "tip") {
        emitter.emit<Message>(EVENTS.STREAM_TIP, message);
      }
      if (!scrollNode.value) return;
      const bottomScrollTopPosition = scrollNode.value.scrollHeight - scrollNode.value.offsetHeight;
      if (message.senderId === authUser.value?.id || bottomScrollTopPosition - scrollNode.value.scrollTop <= 100) {
        shouldScrollToBottom.value = true;
      }
    };

    const onFocus = () => {
      if (props.me && props.me.muted) {
        emit("mutedFocus");
        const txtArea = getElement(baseSendMessage.value).querySelector("textarea");
        txtArea && txtArea.blur();
      }
    };

    const createJoinMessage = (data?: StreamUserJoinData) => {
      if (!data || !data.user || checkIsAdmin(data.user) || !data.threadId || data.threadId !== props.threadId) return;
      const joinMessage: Message = {
        id: new Date().getTime().toFixed(0),
        senderId: "0",
        senderUser: data.user.user,
        text: $t("{0} joined", [data.user.user ? data.user.user.screenName : $t("Guest user #{0}", [data.user.id])]),
        details: { type: "join" },
        threadId: "",
        time: new Date().toISOString(),
        link: null,
        attachment: [],
      };
      onNewMessageReceived(joinMessage);
    };
    const subscribeLiveChatNewMessages = async () => {
      const subscription = await providers.LivechatsProvider.subscribeMessageReceived(
        props.token,
        onNewMessageReceived
      );
      unsubscribe = () => subscription.unsubscribe();
    };

    onMounted(() => {
      subscribeLiveChatNewMessages();
      emitter.on<StreamUserJoinData>(EVENTS.STREAM_USER_JOIN, createJoinMessage);
    });

    onUnmounted(() => {
      emitter.off<StreamUserJoinData>(EVENTS.STREAM_USER_JOIN, createJoinMessage);
      return unsubscribe();
    });

    return {
      baseSendMessage,
      messageList,
      sending,
      messageListNode,
      scrollbarWidth,
      value,
      canTip,
      TipSubjectType,
      onFocus,
    };
  },
});
